import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, allComics } from '@/mocks/comics';

const categoryPageSchema = (catName: string, slug: string) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: `${catName}漫画 - 免费在线下拉式阅读 - 土豪漫画网`,
  description: `土豪漫画网${catName}漫画分类，提供海量免费${catName}漫画在线阅读，支持下拉式浏览，更新快，画质清晰。`,
  url: `https://tuhaomanhua.com/category/${slug}`,
  isPartOf: {
    '@type': 'WebSite',
    name: '土豪漫画网',
    url: 'https://tuhaomanhua.com',
  },
});

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'rexue');
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const categoryInfo = categories.find((c) => c.slug === activeCategory) || categories[0];
  const filteredComics = allComics.filter((c) => c.categorySlug === activeCategory);

  const sortedComics = [...filteredComics].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'latest') return (b.chapters || 0) - (a.chapters || 0);
    return parseInt(b.views) - parseInt(a.views);
  });

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categoryPageSchema(categoryInfo.name, activeCategory)) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '漫画分类', href: '/category/rexue' },
                { label: categoryInfo.name },
              ]}
            />
            <div className="mt-6">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 whitespace-nowrap" style={{ fontFamily: 'var(--font-heading)' }}>
                {categoryInfo.name}漫画
              </h1>
              <p className="text-sm text-foreground-600 mt-2">
                共 {filteredComics.length} 部{categoryInfo.name}漫画，免费下拉式阅读
              </p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-600'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-3 mt-6 mb-8">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
              {[
                { key: 'popular', label: '人气最高' },
                { key: 'rating', label: '评分最高' },
                { key: 'latest', label: '最新更新' },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key as typeof sortBy)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    sortBy === s.key
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {filteredComics.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
                <p className="text-foreground-500 mt-4">该分类暂无漫画，请浏览其他分类</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6" data-product-shop>
                {sortedComics.map((comic) => (
                  <ComicCard key={comic.id} {...comic} />
                ))}
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}